import React, { useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import type { Competition } from '@/types/domain';
import { getLeagueLogoUrl } from '@/utils/pickem-helpers';
import { generateAdDivId } from '@/config/ads.config';
import { useTenant } from '@/contexts/TenantContext';
import { queryKeys } from '@/api/react-query.config';
import GameCard from '@/components/game/GameCard';
import GameCardSkeleton from '@/components/game/GameCardSkeleton';
import EmptyState from '@/components/shared/EmptyState';
import AdSlot from '@/components/ads/AdSlot';

export type CompetitionGameListVariant = 'lobby' | 'myPicks' | 'recent';

export interface CompetitionGameListProps {
  games: Competition[];
  isLoading: boolean;
  skeletonCount?: number;
  emptyTitle: string;
  emptySubtitle?: string;
  emptyActionLabel?: string;
  onEmptyAction?: () => void;
  showEmptyIcon?: boolean;
  showBodyAds: boolean;
  variant: CompetitionGameListVariant;
  onGameAction: (gameId: string) => void;
  onViewLeaderboard: (gameId: string) => void;
  onMenuClick: (gameId: string) => void;
}

const CompetitionGameList: React.FC<CompetitionGameListProps> = ({
  games,
  isLoading,
  skeletonCount = 3,
  emptyTitle,
  emptySubtitle,
  emptyActionLabel,
  onEmptyAction,
  showEmptyIcon = true,
  showBodyAds,
  variant,
  onGameAction,
  onViewLeaderboard,
  onMenuClick,
}) => {
  const { adPlacements } = useTenant();
  const queryClient = useQueryClient();
  const handleCountdownComplete = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.pickem.all });
  }, [queryClient]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[...Array(skeletonCount)].map((_, index) => (
          <motion.div
            key={`skeleton-${index}`}
            initial={variant === 'myPicks' ? { opacity: 0 } : { opacity: 0, y: 20 }}
            animate={variant === 'myPicks' ? { opacity: 1 } : { opacity: 1, y: 0 }}
            transition={
              variant === 'myPicks'
                ? { delay: index * 0.1 }
                : { delay: index * 0.05, duration: 0.3 }
            }
          >
            <GameCardSkeleton />
          </motion.div>
        ))}
      </div>
    );
  }

  if (games.length === 0) {
    return (
      <EmptyState
        title={emptyTitle}
        subtitle={emptySubtitle}
        showAdSpace={false}
        showIcon={showEmptyIcon}
        actionLabel={emptyActionLabel}
        onAction={onEmptyAction}
      />
    );
  }

  return (
    <div className="space-y-6">
      <AnimatePresence mode="popLayout">
        {games.map((game, index) => (
          <React.Fragment key={`game-${game.id}`}>
            <motion.div
              key={game.id}
              layout={variant === 'myPicks'}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={variant === 'myPicks' ? { opacity: 0, scale: 0.95 } : { opacity: 0, y: -20 }}
              transition={
                variant === 'myPicks'
                  ? { duration: 0.3, delay: index * 0.05 }
                  : { delay: index * 0.05, duration: 0.3 }
              }
            >
              <GameCard
                id={game.id}
                title={game.title}
                prize={game.prize}
                state={game.state}
                gameDate={game.gameDate}
                hasSelections={game.hasSelections}
                isOpen={game.isOpen}
                totalPicks={game.questions.length}
                totalEntries={game.totalEntries}
                entryCost="FREE"
                leagueLogoUrl={getLeagueLogoUrl(game.sport)}
                currentPoints={game.currentPoints}
                finalScore={game.finalScore}
                myPrize={game.myPrize}
                {...(variant !== 'recent'
                  ? {
                      isLocked: !game.isOpen,
                      onEdit: () => onGameAction(game.id),
                    }
                  : {})}
                {...(variant !== 'lobby'
                  ? {
                      correctPicks: game.correctPicks,
                    }
                  : {})}
                onPlay={variant === 'lobby' ? () => onGameAction(game.id) : undefined}
                onView={() => onGameAction(game.id)}
                onViewLeaderboard={() => onViewLeaderboard(game.id)}
                onMenuClick={() => onMenuClick(game.id)}
                onCountdownComplete={
                  game.state === 'countdown' ? handleCountdownComplete : undefined
                }
              />
            </motion.div>

            {showBodyAds && adPlacements && index === 2 && (
              <motion.div
                key="ad-0"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: (index + 1) * 0.05, duration: 0.3 }}
                className="my-6"
              >
                <AdSlot
                  divId={generateAdDivId(adPlacements, 'BODY', true, 0)}
                  adPlacement={adPlacements.BODY}
                  placement="lobby-after-contest-3"
                  className="rounded-lg overflow-hidden"
                />
              </motion.div>
            )}
          </React.Fragment>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default CompetitionGameList;
